/**
 * Hook para gestionar métodos de pago disponibles
 * Sincroniza con configuración del backend (/api/payments/methods/config)
 */
import { useEffect, useState } from 'react'
import {
  PAYMENT_METHODS,
  getEnabledPaymentMethods,
  getPaymentMethodsInDevelopment,
  setPaymentMethodEnabled,
  PaymentMethodConfig,
  PaymentMethodType
} from '../config/paymentMethods'
import apiClient from '@/services/apiClient'

interface BackendPaymentConfig {
  [key: string]: {
    enabled: boolean
    configured: boolean
    requires_api_key: boolean
  }
}

export interface UsePaymentMethodsReturn {
  enabledMethods: PaymentMethodConfig[]
  developmentMethods: PaymentMethodConfig[]
  allMethods: PaymentMethodConfig[]
  isMethodEnabled: (methodId: PaymentMethodType) => boolean
  enableMethod: (methodId: PaymentMethodType) => void
  disableMethod: (methodId: PaymentMethodType) => void
  getMethodConfig: (methodId: PaymentMethodType) => PaymentMethodConfig | undefined
  loading: boolean
}

export const usePaymentMethods = (): UsePaymentMethodsReturn => {
  const [enabledMethods, setEnabledMethods] = useState<PaymentMethodConfig[]>([])
  const [developmentMethods, setDevelopmentMethods] = useState<PaymentMethodConfig[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // ✅ Sincronizar configuración con el backend
    const syncWithBackend = async () => {
      try {
        setLoading(true)
        const response = await apiClient.get<BackendPaymentConfig>('/api/payments/methods/config')
        const config = response.data

        // Actualizar configuración de cada método
        Object.keys(config).forEach((methodKey) => {
          const methodId = methodKey as PaymentMethodType
          const backendConfig = config[methodKey]

          if (backendConfig && PAYMENT_METHODS[methodId]) {
            setPaymentMethodEnabled(methodId, backendConfig.enabled)
          }
        })

        // Actualizar estados locales
        setEnabledMethods(getEnabledPaymentMethods())
        setDevelopmentMethods(getPaymentMethodsInDevelopment())
      } catch (error) {
        console.error('Error syncing payment methods with backend:', error)
        // Fallback: usar configuración local
        setEnabledMethods(getEnabledPaymentMethods())
        setDevelopmentMethods(getPaymentMethodsInDevelopment())
      } finally {
        setLoading(false)
      }
    }

    syncWithBackend()
  }, [])

  const isMethodEnabled = (methodId: PaymentMethodType): boolean => {
    const method = PAYMENT_METHODS[methodId]
    return method?.enabled ?? false
  }

  const enableMethod = (methodId: PaymentMethodType): void => {
    setPaymentMethodEnabled(methodId, true)
    setEnabledMethods(getEnabledPaymentMethods())
    setDevelopmentMethods(getPaymentMethodsInDevelopment())
  }

  const disableMethod = (methodId: PaymentMethodType): void => {
    setPaymentMethodEnabled(methodId, false)
    setEnabledMethods(getEnabledPaymentMethods())
    setDevelopmentMethods(getPaymentMethodsInDevelopment())
  }

  const getMethodConfig = (methodId: PaymentMethodType): PaymentMethodConfig | undefined => {
    return PAYMENT_METHODS[methodId]
  }

  return {
    enabledMethods,
    developmentMethods,
    allMethods: Object.values(PAYMENT_METHODS),
    isMethodEnabled,
    enableMethod,
    disableMethod,
    getMethodConfig,
    loading
  }
}
